import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import heroImage from "../assets/ai-interview.jpg";

const WelcomePage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isLoggedIn = !!user && !!user.id;
  const userName = user.full_name || '';

  return (
    <div style={styles.pageWrapper}>
      <SEO 
        title="Smart Interview - AI Talent Acquisition Suite & Tech Assessments" 
        description="Automate screening, schedule seamlessly, and get deep behavioral insights for every candidate. Your smart command center for talent acquisition."
        keywords="AI interview, smart proctoring, tech assessment, candidate screening, developer screening, video interview"
      />
      
      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Smart Interview Platform",
          "operatingSystem": "All",
          "applicationCategory": "BusinessApplication",
          "browserRequirements": "Requires HTML5 and JavaScript. WebRTC support required.",
          "offers": {
            "@type": "Offer",
            "price": "0.00",
            "priceCurrency": "USD"
          },
          "description": "Enterprise-grade multi-tenant smart assessment platform featuring automated AI screening, live proctored coding sandboxes, keystroke playback, and behavioral metrics.",
          "applicationSubCategory": "Recruitment Assessment Software"
        })}
      </script>

      <Navbar isLoggedIn={isLoggedIn} userName={userName} />

      {/* HERO SECTION */}
      <main style={styles.heroWrapper}>
        {/* Left Column: Value Proposition & CTAs */}
        <section style={styles.heroLeft}>
          <div style={styles.pillTag}>
            <span style={styles.pillSparkle}>✨</span>
            <span style={styles.pillText}>Next-Gen Talent Acquisition Suite</span>
          </div>

          <h1 style={styles.heroTitle}>
            Hire Faster with{' '}
            <span style={styles.gradientTitle}>Smart Interview</span>
          </h1>

          <p style={styles.heroSubtitle}>
            Automate technical screening, coordinate live assessments in a proctored collaborative IDE, and obtain deep AI-driven behavioral scorecards. The complete command center for modern developer recruitment.
          </p>

          <div style={styles.ctaGroup}>
            <button
              onClick={() => navigate(isLoggedIn ? '/interview' : '/login')}
              style={styles.primaryCta}
            >
              Get Started Free →
            </button>
            <button
              onClick={() => navigate('/how')}
              style={styles.secondaryCta}
            >
              See How It Works
            </button>
          </div>

          <div style={styles.trustBadges}>
            <div style={styles.trustItem}>
              <span style={styles.trustCheck}>✓</span>
              <span>AI-Proctored Video</span>
            </div>
            <div style={styles.trustItem}>
              <span style={styles.trustCheck}>✓</span>
              <span>Live Collaborative IDE</span>
            </div>
            <div style={styles.trustItem}>
              <span style={styles.trustCheck}>✓</span>
              <span>Automated Scorecards</span>
            </div>
          </div>
        </section>

        {/* Right Column: Enhanced Image with Ambient Glow & Floating Cards */}
        <section style={styles.heroRight}>
          <div style={styles.imageWrapper}>
            {/* Ambient Backlight Glow to bridge dark image to light background */}
            <div style={styles.ambientGlow} />

            {/* Elevated Window Card Container */}
            <div style={styles.windowCard}>
              {/* macOS Style Application Titlebar */}
              <div style={styles.windowHeader}>
                <div style={styles.trafficDots}>
                  <span style={{ ...styles.trafficDot, backgroundColor: '#ff5f56' }} />
                  <span style={{ ...styles.trafficDot, backgroundColor: '#ffbd2e' }} />
                  <span style={{ ...styles.trafficDot, backgroundColor: '#27c93f' }} />
                </div>
                <div style={styles.windowAddress}>
                  <span style={styles.lockIcon}>🔒</span>
                  <span>smartinterview.ai/talent-suite</span>
                </div>
                <div style={styles.windowLiveBadge}>
                  <span style={styles.livePulse} />
                  <span>LIVE ASSESSMENT</span>
                </div>
              </div>

              {/* The Hero Image */}
              <img
                src={heroImage}
                alt="Smart Interview - AI Talent Acquisition Suite Dashboard"
                style={styles.heroImage}
              />
            </div>

            {/* Floating Glassmorphic Pill - Top Right */}
            <div style={styles.floatingPillTop}>
              <div style={styles.pillIconWrap}>🎯</div>
              <div>
                <div style={styles.pillLabel}>Overall Fit</div>
                <div style={styles.pillValue}>95% Match • Top 1%</div>
              </div>
            </div>

            {/* Floating Glassmorphic Pill - Bottom Left */}
            <div style={styles.floatingPillBottom}>
              <div style={styles.pillIconWrapShield}>🛡️</div>
              <div>
                <div style={styles.pillLabel}>Real-time Proctoring</div>
                <div style={styles.pillValueSuccess}>Gaze & Audio Verified</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Internal CSS for Animations */}
      <style>{`
        @keyframes pulseDot {
          0% { transform: scale(0.95); opacity: 0.8; }
          50% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(0.95); opacity: 0.8; }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes floatSlowReverse {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(6px); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    background: 'var(--bg-page-alt)',
    padding: 0,
    overflowX: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    color: 'var(--text-primary)',
  },

  heroWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1360px',
    margin: '30px auto 60px',
    padding: '20px 24px',
    gap: '48px',
  },

  heroLeft: {
    flex: '1 1 480px',
    maxWidth: '560px',
  },

  pillTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 14px',
    borderRadius: '30px',
    background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(59, 130, 246, 0.08) 100%)',
    border: '1px solid rgba(99, 102, 241, 0.25)',
    marginBottom: '20px',
  },

  pillSparkle: {
    fontSize: '14px',
  },

  pillText: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#4f46e5',
    letterSpacing: '0.02em',
  },

  heroTitle: {
    fontSize: 'clamp(38px, 4vw, 54px)',
    fontWeight: '800',
    lineHeight: '1.15',
    color: 'var(--text-primary)',
    marginBottom: '20px',
    letterSpacing: '-0.02em',
  },

  gradientTitle: {
    background: 'linear-gradient(135deg, #4f46e5 0%, #2563eb 50%, #06b6d4 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  heroSubtitle: {
    fontSize: '17px',
    color: 'var(--text-tertiary)',
    lineHeight: '1.65',
    marginBottom: '32px',
  },

  ctaGroup: {
    display: 'flex',
    gap: '14px',
    flexWrap: 'wrap',
    marginBottom: '36px',
  },

  primaryCta: {
    padding: '14px 28px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 25px -4px rgba(79, 70, 229, 0.38)',
    transition: 'all 0.2s ease',
  },

  secondaryCta: {
    padding: '14px 24px',
    borderRadius: '12px',
    border: '1px solid var(--border-color, #cbd5e1)',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #334155)',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
  },

  trustBadges: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    paddingTop: '20px',
    borderTop: '1px solid rgba(203, 213, 225, 0.5)',
  },

  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted, #64748b)',
  },

  trustCheck: {
    color: '#10b981',
    fontWeight: '800',
  },

  heroRight: {
    flex: '1 1 560px',
    maxWidth: '680px',
    position: 'relative',
  },

  imageWrapper: {
    position: 'relative',
    width: '100%',
  },

  ambientGlow: {
    position: 'absolute',
    top: '-8%',
    left: '-8%',
    right: '-8%',
    bottom: '-8%',
    background: 'radial-gradient(ellipse at center, rgba(79, 70, 229, 0.2) 0%, rgba(37, 99, 235, 0.12) 45%, transparent 70%)',
    filter: 'blur(40px)',
    zIndex: 0,
    pointerEvents: 'none',
  },

  windowCard: {
    position: 'relative',
    zIndex: 1,
    borderRadius: '18px',
    overflow: 'hidden',
    backgroundColor: '#0f172a',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.28), 0 0 40px rgba(79, 70, 229, 0.15)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  windowHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },

  trafficDots: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },

  trafficDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-block',
  },

  windowAddress: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    fontSize: '11px',
    color: '#94a3b8',
    fontWeight: '500',
    letterSpacing: '0.02em',
  },

  lockIcon: {
    fontSize: '10px',
    opacity: 0.8,
  },

  windowLiveBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '10px',
    fontWeight: '700',
    color: '#22c55e',
    letterSpacing: '0.06em',
  },

  livePulse: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    display: 'inline-block',
    animation: 'pulseDot 1.8s infinite ease-in-out',
  },

  heroImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
    objectFit: 'cover',
  },

  floatingPillTop: {
    position: 'absolute',
    top: '-18px',
    right: '-14px',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    borderRadius: '14px',
    background: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
    animation: 'floatSlow 4s ease-in-out infinite',
  },

  floatingPillBottom: {
    position: 'absolute',
    bottom: '-18px',
    left: '-14px',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    borderRadius: '14px',
    background: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
    animation: 'floatSlowReverse 4.5s ease-in-out infinite',
  },

  pillIconWrap: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    background: 'rgba(79, 70, 229, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },

  pillIconWrapShield: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    background: 'rgba(16, 185, 129, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },

  pillLabel: {
    fontSize: '10px',
    textTransform: 'uppercase',
    fontWeight: '700',
    color: '#64748b',
    letterSpacing: '0.04em',
  },

  pillValue: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#0f172a',
  },

  pillValueSuccess: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#059669',
  },
};

export default WelcomePage;

